import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import LogoutIcon from '@mui/icons-material/Logout';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import authService from '../services/AuthService';
import apiService from '../services/ApiService';
import LoadingSpinner from '../components/LoadingSpinner';
import ActivityCalendar from '../components/ActivityCalendar';
import ConfirmDialog from '../components/ConfirmDialog';
import useCountUp from '../hooks/useCountUp';

export default function ProfilePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState(authService.getUser());
  const [loading, setLoading] = useState(!user);
  const [stats, setStats] = useState(null);
  const [activityData, setActivityData] = useState([]);
  const [activityYear, setActivityYear] = useState(new Date().getFullYear());
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [nameError, setNameError] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);

  useEffect(() => {
    if (!user) {
      authService
        .refreshMe()
        .then((u) => setUser(u))
        .finally(() => setLoading(false));
    }
    apiService
      .get('/api/v1/users/me/stats')
      .then((data) => setStats(data))
      .catch(() => setStats({ gamesPlayed: 0, totalScore: 0, bestScore: 0 }));
  }, [user]);

  useEffect(() => {
    apiService
      .get(`/api/v1/game/activity?year=${activityYear}`)
      .then((data) => setActivityData(Array.isArray(data) ? data : []))
      .catch(() => setActivityData([]));
  }, [activityYear]);

  const handleStartEditName = () => {
    setNameInput(user?.name || '');
    setNameError('');
    setEditingName(true);
  };

  const handleSaveName = async () => {
    if (!nameInput.trim()) {
      setNameError(t('settings.nameErrorTooShort'));
      return;
    }
    setSavingName(true);
    setNameError('');
    try {
      const updatedUser = await apiService.put('/api/v1/users/me/name', { name: nameInput.trim() });
      authService.updateUser(updatedUser);
      setUser(updatedUser);
      setEditingName(false);
      setSnackbar({ open: true, message: t('settings.nameSaved'), severity: 'success' });
    } catch (err) {
      const code = err?.code;
      if (code === 2014) setNameError(t('settings.nameErrorTooShort'));
      else if (code === 2015) setNameError(t('settings.nameErrorTooLong'));
      else if (code === 2016) setNameError(t('settings.nameErrorEmail'));
      else if (code === 2017) setNameError(t('settings.nameErrorUrl'));
      else setNameError(t('settings.nameErrorGeneric'));
    } finally {
      setSavingName(false);
    }
  };

  const handleLogout = async () => {
    await authService.logout();
    navigate('/login', { replace: true });
  };

  const handleDeleteAccount = async () => {
    setDeletingAccount(true);
    try {
      await authService.deleteAccount();
      navigate('/login', { replace: true });
    } catch (err) {
      setSnackbar({
        open: true,
        message: t(`apiErrors.${err.code}`, t('profile.deleteAccountError')),
        severity: 'error',
      });
      setDeleteOpen(false);
    } finally {
      setDeletingAccount(false);
    }
  };

  // msPerUnit escala la duración con el valor: bestScore (máx 15) irá despacio,
  // totalScore (potencialmente cientos) irá a un ritmo proporcional.
  const animatedGamesPlayed = useCountUp(stats?.gamesPlayed ?? null, { msPerUnit: 30,  minDuration: 1200, maxDuration: 3000, delay: 0   });
  const animatedTotalScore  = useCountUp(stats?.totalScore  ?? null, { msPerUnit: 8,   minDuration: 1400, maxDuration: 3500, delay: 150 });
  const animatedBestScore   = useCountUp(stats?.bestScore   ?? null, { msPerUnit: 180, minDuration: 1500, maxDuration: 2800, delay: 300 });

  if (loading) return <LoadingSpinner />;

  const initials = user?.name
    ? user.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  return (
    <Box className="page-content" sx={{ gap: 2, maxWidth: 500, mx: 'auto', width: '100%' }}>
      <Typography
        variant="h5"
        fontWeight={700}
        textAlign="center"
        color="white"
        sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
      >
        {t('profile.title')}
      </Typography>

      <Card sx={{ width: '100%' }}>
        <CardContent>
          {/* Avatar + nombre editable */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2, gap: 2 }}>
            <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main', fontSize: '1.8rem' }}>
              {initials}
            </Avatar>
            <Box textAlign="center" sx={{ width: '100%' }}>
              {editingName ? (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', justifyContent: 'center' }}>
                  <TextField
                    size="small"
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    placeholder={t('settings.editNamePlaceholder')}
                    error={!!nameError}
                    helperText={nameError}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveName();
                      if (e.key === 'Escape') setEditingName(false);
                    }}
                    inputProps={{ maxLength: 50 }}
                    sx={{ width: 220 }}
                  />
                  <IconButton size="small" color="primary" onClick={handleSaveName} disabled={savingName} sx={{ mt: 0.5 }}>
                    <CheckIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setEditingName(false)} sx={{ mt: 0.5 }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                  <Typography variant="h6" fontWeight={700}>
                    {user?.name || t('common.unknown')}
                  </Typography>
                  <IconButton size="small" onClick={handleStartEditName} sx={{ p: 0.25 }}>
                    <EditIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Box>
              )}
              <Typography variant="body2" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            {t('profile.stats')}
          </Typography>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={4} textAlign="center">
              <Typography variant="h4" fontWeight={700} color="primary">
                {animatedGamesPlayed ?? '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('profile.gamesPlayed')}
              </Typography>
            </Grid>
            <Grid item xs={4} textAlign="center">
              <Typography variant="h4" fontWeight={700} color="secondary">
                {animatedTotalScore ?? '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('profile.totalScore')}
              </Typography>
            </Grid>
            <Grid item xs={4} textAlign="center">
              <Typography variant="h4" fontWeight={700} color="warning.main">
                {animatedBestScore ?? '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('profile.bestScore')}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            {t('profile.activityCalendar')}
          </Typography>
          <ActivityCalendar
            data={activityData}
            year={activityYear}
            onYearChange={setActivityYear}
          />

          <Divider sx={{ my: 2 }} />

          <Button
            variant="outlined"
            color="error"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            fullWidth
          >
            {t('profile.logout')}
          </Button>

          <Button
            variant="text"
            color="error"
            startIcon={<DeleteForeverIcon />}
            onClick={() => setDeleteOpen(true)}
            fullWidth
            sx={{ mt: 1 }}
          >
            {t('profile.deleteAccount')}
          </Button>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={deleteOpen}
        title={t('profile.deleteAccountConfirmTitle')}
        message={t('profile.deleteAccountConfirmMessage')}
        confirmLabel={t('profile.deleteAccountButton')}
        loading={deletingAccount}
        onConfirm={handleDeleteAccount}
        onClose={() => setDeleteOpen(false)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
